const RESUME = /\.(pdf|docx|doc)$/i;
const JUNK = /(^|\/)(__MACOSX|\.)/;
const isZip = (f) => /\.zip$/i.test(f.name) || ["application/zip", "application/x-zip-compressed"].includes(f.type);

// Drop -> [{file, depth}] where depth 0 = directly inside what was dropped (dropped files or a dropped folder's own files).
const walk = (entry, depth) => new Promise((res) => {
  if (entry.isFile) return entry.file((f) => res([{ file: f, depth }]), () => res([]));
  const r = entry.createReader(); const all = [];
  const next = () => r.readEntries(async (ents) => {
    if (!ents.length) return res((await Promise.all(all.map((e) => walk(e, depth + 1)))).flat());
    all.push(...ents); next();
  }, () => res([]));
  next();
});

const fromDrop = async (dt) => {
  const ents = [...(dt.items || [])].map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
  if (!ents.length) return [...(dt.files || [])].map((f) => ({ file: f, depth: 0 }));
  return (await Promise.all(ents.map((e) => (e.isFile ? walk(e, 0) : walk(e, -1))))).flat();
};

// Picked files or a picked folder (webkitRelativePath "Folder/a.pdf" = top level).
const fromInput = (files) => [...files].map((f) => ({ file: f, depth: f.webkitRelativePath ? f.webkitRelativePath.split("/").length - 2 : 0 }));

const unzip = async (f) => {
  const JSZip = (await import("jszip")).default;
  const z = await JSZip.loadAsync(f);
  const ents = Object.values(z.files).filter((e) => !e.dir && !JUNK.test(e.name));
  const roots = new Set(ents.map((e) => (e.name.includes("/") ? e.name.split("/")[0] : "")));
  const strip = roots.size === 1 && !roots.has("") ? 1 : 0;
  return ents.map((e) => ({ name: e.name, depth: e.name.split("/").length - 1 - strip, entry: e }));
};

// Top level only: returns {resumes: File[], skipped: [{name, reason}]} from a drop event or a FileList.
export const collectResumes = async (src, isDrop) => {
  const items = isDrop ? await fromDrop(src) : fromInput(src || []);
  const resumes = []; const skipped = [];
  for (const { file, depth } of items) {
    const path = file.webkitRelativePath || file.name;
    if (JUNK.test(path)) continue;
    if (depth > 0) { skipped.push({ name: path, reason: "In a subfolder" }); continue; }
    if (isZip(file)) {
      try {
        for (const x of await unzip(file)) {
          if (x.depth > 0) skipped.push({ name: `${file.name} › ${x.name}`, reason: "In a subfolder" });
          else if (!RESUME.test(x.name)) skipped.push({ name: `${file.name} › ${x.name}`, reason: "Not a resume file" });
          else resumes.push(new File([await x.entry.async("blob")], x.name.split("/").pop()));
        }
      } catch { skipped.push({ name: file.name, reason: "Couldn't open this ZIP" }); }
    } else if (RESUME.test(file.name)) resumes.push(file);
    else skipped.push({ name: path, reason: "Not a resume file" });
  }
  return { resumes, skipped };
};
